import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { formatApiError } from "@/lib/api";
import { COUNTRIES_FULL, DEVELOPER_COUNTRIES, COUNTRY_TO_DIAL, COUNTRY_TO_FLAG } from "@/lib/dialCodes";
import Logo from "@/components/Logo";
import PhoneInput from "@/components/PhoneInput";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { ArrowRight, User, Building2, Check, Mail, FlaskConical } from "lucide-react";
import { useAppConfig } from "@/lib/config";

export default function Signup() {
  const { register, resendVerification } = useAuth();
  const [role, setRole] = useState("investor");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    country: "",
    email: "",
    password: "",
    confirm: "",
    company_name: "",
    company_registration: "",
    years_in_business: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // {email}
  const [resending, setResending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cfg = useAppConfig();

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    if (form.password !== form.confirm) {
      setErr("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: form.email,
        password: form.password,
        role,
        name: form.name,
        phone: form.phone,
        country: form.country,
        company_name: role === "developer" ? form.company_name : undefined,
        company_registration: role === "developer" ? form.company_registration : undefined,
        years_in_business: role === "developer" ? form.years_in_business : undefined,
      };
      const res = await register(payload);
      setSent({ email: res.email || form.email });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!sent?.email) return;
    setResending(true);
    try {
      await resendVerification(sent.email);
    } finally {
      setResending(false);
    }
  }

  if (cfg?.demo_mode) {
    return (
      <div className="min-h-screen bg-white">
        <header className="px-6 sm:px-12 py-6 flex items-center justify-between border-b border-neutral-100">
          <Logo />
          <Link to="/login" data-testid="goto-login" className="text-sm text-propera font-semibold hover:underline">Sign In</Link>
        </header>
        <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="demo-signup-closed">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mb-6"><FlaskConical size={26} /></div>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">This is the Propera demo</h1>
          <p className="text-neutral-500 mt-3">New accounts can&apos;t be created here. Sign in with a demo account to explore, or create your real account on the live platform.</p>
          <div className="mt-8 space-y-3">
            {cfg.live_url && <a href={`${cfg.live_url}/signup`} data-testid="demo-signup-live-link" className="block w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition-colors">Create an account on the live platform</a>}
            <Link to="/login" data-testid="demo-signup-login" className="block text-sm text-neutral-600 hover:text-propera">Explore the demo — sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-white">
        <header className="px-6 sm:px-12 py-6 flex items-center justify-between border-b border-neutral-100">
          <Logo />
          <Link to="/login" data-testid="goto-login" className="text-sm text-propera font-semibold hover:underline">Sign In</Link>
        </header>
        <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="signup-verify-prompt">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-6">
            <Mail size={26} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">Check your inbox</h1>
          <p className="text-neutral-500 mt-3">
            We&apos;ve sent a verification link to <b className="text-neutral-900">{sent.email}</b>. Tap it to activate your Propera account — the link is valid for 24 hours.
          </p>
          <div className="mt-8 space-y-3">
            <button data-testid="resend-verification-btn" onClick={handleResend} disabled={resending} className="w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition disabled:opacity-60">
              {resending ? "Sending..." : "Resend verification email"}
            </button>
            <Link to="/login" data-testid="signup-verify-back-login" className="block text-sm text-neutral-600 hover:text-propera">
              I&apos;ll verify later — back to sign in
            </Link>
          </div>
          <p className="text-[11px] text-neutral-400 mt-8">
            Wrong email? <Link to="/signup" data-testid="signup-verify-restart" onClick={() => setSent(null)} className="text-propera hover:underline">Sign up again</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="px-6 sm:px-12 py-6 flex items-center justify-between border-b border-neutral-100">
        <Logo />
        <Link to="/login" data-testid="goto-login" className="text-sm text-propera font-semibold hover:underline">Sign In</Link>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.2em] text-propera font-semibold mb-3">Create Account</p>
        <h1 className="text-4xl font-bold text-neutral-900 leading-tight">
          Join the <span className="serif italic text-propera-gold">diaspora</span> shaping cities back home.
        </h1>
        <p className="text-neutral-500 mt-3 max-w-lg">Thousands of investors and developers building wealth across Africa.</p>

        <form onSubmit={onSubmit} className="mt-10 space-y-6">
          {/* Role selector */}
          <div>
            <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">I am a...</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <RoleCard
                testId="role-investor"
                active={role === "investor"}
                onClick={() => setRole("investor")}
                icon={User}
                title="Individual Investor"
                subtitle="Invest in verified property developments"
              />
              <RoleCard
                testId="role-developer"
                active={role === "developer"}
                onClick={() => setRole("developer")}
                icon={Building2}
                title="Property Developer"
                subtitle="List properties for diaspora investment"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input testId="signup-name" label={role === "developer" ? "Contact Person Name" : "Full Name"} required value={form.name} onChange={(v) => set("name", v)} />
            {role === "developer" && (
              <Input testId="signup-company" label="Company Name" required value={form.company_name} onChange={(v) => set("company_name", v)} />
            )}
            <PhoneInput
              testId="signup-phone"
              label="Phone Number"
              value={form.phone}
              onChange={(v) => set("phone", v)}
              defaultDial={COUNTRY_TO_DIAL[form.country] || "+234"}
            />
            {role === "developer" && (
              <Input testId="signup-reg" label="Company Registration No." value={form.company_registration} onChange={(v) => set("company_registration", v)} placeholder="e.g., RC123456" />
            )}
            {role === "developer" && (
              <Input testId="signup-years" label="Years in Business" value={form.years_in_business} onChange={(v) => set("years_in_business", v)} placeholder="e.g., 5" />
            )}
            <div>
            <label className="text-xs font-semibold text-neutral-700">Country *</label>
            <select
              data-testid="signup-country"
              required
              value={form.country}
              onChange={(e) => set("country", e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition bg-white"
            >
              <option value="">{role === "developer" ? "Select country (Africa)" : "Select your country"}</option>
              {(role === "developer" ? DEVELOPER_COUNTRIES : COUNTRIES_FULL).map((c) => (
                <option key={c} value={c}>{COUNTRY_TO_FLAG[c] || ""} {c}</option>
              ))}
            </select>
            {role === "developer" && (
              <p className="text-[11px] text-neutral-500 mt-1.5">Developers can only list properties in Nigeria, Ghana &amp; Kenya for now.</p>
            )}
          </div>
            <Input testId="signup-email" label="Email" type="email" required value={form.email} onChange={(v) => set("email", v)} />
            <Input testId="signup-password" label="Password" type="password" required value={form.password} onChange={(v) => set("password", v)} placeholder="At least 8 characters" />
            <Input testId="signup-confirm" label="Confirm Password" type="password" required value={form.confirm} onChange={(v) => set("confirm", v)} />
          </div>

          {err && (
            <div data-testid="signup-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{err}</div>
          )}

          <button
            data-testid="signup-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3.5 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60 glow-primary"
          >
            {loading ? "Creating account..." : (<>Create Account <ArrowRight size={16} /></>)}
          </button>

          {role === "investor" && (
            <>
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-neutral-200" />
                <span className="text-[11px] uppercase tracking-widest text-neutral-400">or</span>
                <div className="flex-1 h-px bg-neutral-200" />
              </div>
              <GoogleSignInButton label="Sign up with Google" testId="signup-google" />
            </>
          )}
        </form>

        <p className="text-sm text-neutral-500 mt-8 text-center">
          Already have an account? <Link to="/login" className="text-propera font-semibold hover:underline">Sign In</Link>
        </p>

        <div className="mt-6 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-400 justify-center">
          <span>By creating an account you agree to our</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-propera hover:underline">Terms</a>
          <span>and</span>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-propera hover:underline">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ testId, active, onClick, icon: Icon, title, subtitle }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left p-5 rounded-2xl border-2 transition-all relative ${
        active ? "border-propera bg-propera-light/40" : "border-neutral-200 hover:border-neutral-300"
      }`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${active ? "bg-propera text-white" : "bg-neutral-100 text-neutral-600"}`}>
        <Icon size={20} />
      </div>
      <p className="font-semibold text-neutral-900">{title}</p>
      <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
      {active && <Check size={18} className="absolute top-4 right-4 text-propera" />}
    </button>
  );
}

function Input({ testId, label, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-700">{label}{required && " *"}</label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none transition"
      />
    </div>
  );
}
